import React from "react";

// Think5Whys Anvaya ♥
// Your data companion, every step of the journey
// 🤰
// Journey Data Companion ♥

/**
 * AnvayaHeaderBanner — the brand hero strip at the top of the page.
 * Split out from Anvaya.jsx so the header's look can be tuned on its own
 * without touching the rest of the page.
 */
export default function AnvayaHeaderBanner({
  eyebrow = "Pregnancy Journey Data Companion",
  subtitle = "Always with you, from the first flutter to the first cry",
}) {
  const orbitIcons = [
    // Top
    { id: "hydration", icon: "💧", position: "top-[2%] left-[50%] -translate-x-1/2" },
    // Top-right
    { id: "nutrition", icon: "🥗", position: "top-[12%] right-[8%] sm:top-[10%] sm:right-[12%]" },
    // Right
    { id: "medicine",  icon: "💊", position: "top-[38%] right-[2%] sm:top-[36%] sm:right-[4%]" },
    // Bottom-right
    { id: "mood",      icon: "😊", position: "bottom-[12%] right-[8%] sm:bottom-[10%] sm:right-[12%]" },
    // Bottom
    { id: "sleep",     icon: "🌙", position: "bottom-[2%] left-[50%] -translate-x-1/2" },
    // Bottom-left
    { id: "yoga",      icon: "🧘‍♀️", position: "bottom-[12%] left-[8%] sm:bottom-[10%] sm:left-[12%]" },
    // Left
    { id: "doctor",    icon: "🩺", position: "top-[38%] left-[2%] sm:top-[36%] sm:left-[4%]" },
    // Top-left
    { id: "breathing", icon: "🌬️", position: "top-[12%] left-[8%] sm:top-[10%] sm:left-[12%]" },
  ];

  return (
    <header className="relative z-20 overflow-hidden rounded-b-3xl bg-gradient-to-br from-[#FCE8EF] via-[#F8EDD9] to-[#d6a0f8] px-5 pt-8 pb-10 sm:pt-10 sm:pb-12 text-center shadow-[0_20px_50px_-20px_rgba(180,140,160,0.18)]">
      {/* Soft luminous top accent */}
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#F8C1D0] via-[#F5D5A8] to-[#E8C9F0]" />

      {/* Warm cream ambient glow */}
      <div
        className="absolute -top-20 left-1/2 -translate-x-1/2 h-56 w-80 rounded-full bg-[#FFF8EC]/80 blur-[90px]"
        aria-hidden="true"
      />

      {/* Eyebrow */}
      <div className="relative inline-flex items-center gap-1.5 rounded-full border border-white/60 bg-[#eb88a9] px-3 py-1 text-[0.62rem] font-mono uppercase tracking-[0.2em] text-white">
        <span
          className="h-1.5 w-1.5 rounded-full bg-[#eb88a9] motion-safe:animate-pulse"
          aria-hidden="true"
        />
        {eyebrow}
      </div>

      {/* Brand title */}
      <h1 className="relative mt-3 font-display text-[1.6rem] sm:text-[1.9rem] font-semibold text-[#3D2168]">
        {/* Think5Whys{" "} */}
        <span className="bg-gradient-to-r from-[#C07A9A] via-[#D4A0B8] to-[#B88BC8] bg-clip-text text-transparent">
          AnvayaEhsaas
        </span>{" "}
        <span className="text-[#E07A9A]" aria-hidden="true">
          ♥
        </span>
      </h1>

      {/* Subtitle */}
      <p className="relative mt-2 text-[0.82rem] sm:text-sm text-[#6E5585]">
        {subtitle}
      </p>

      {/* Positioning line — emotional + clear */}
      <p className="relative mt-2.5 mx-auto max-w-md text-[0.78rem] sm:text-[0.84rem] leading-relaxed font-medium text-[#6E5585]">
        Your gentle data companion through pregnancy and the first five years —
        optionally shared with family, doctors & clinics, and trusted pharma
        insights.
      </p>

      {/* Ecosystem note — exciting + trustworthy */}
      <p className="relative mt-1.5 mx-auto max-w-sm text-[0.68rem] sm:text-[0.72rem] leading-snug text-[#6E5585]/85">
        AI prompts that reveal insights from <em>your real journey data</em> —
        never generic GenAI.
        <br />
        Built so every mother and child can feel seen, supported, and safer.
      </p>

      {/* ─────────────────────────────────────────────── */}
      {/* Journey Orbit — icon only, mood-enhancing center */}
      {/* ─────────────────────────────────────────────── */}
      <div className="relative mx-auto mt-8 sm:mt-10 aspect-square w-full max-w-[340px] sm:max-w-[400px]">
        {/* Soft warm cream + lavender atmosphere around the mother */}
        <div
          className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,#FFF8EC_0%,#F8E9FF55_40%,transparent_70%)] blur-2xl"
          aria-hidden="true"
        />
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-48 w-48 sm:h-56 sm:w-56 rounded-full bg-gradient-to-br from-[#FFF0E8]/70 via-[#F8E0F0]/40 to-[#EEDCFA]/50 blur-3xl motion-safe:animate-pulse"
          aria-hidden="true"
        />

        {/* Soft connecting lines from center to each icon */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-0"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* Soft gradient definition */}
          <defs>
            <linearGradient
              id="lineGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#E8A0B8" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#D4C0F0" stopOpacity="0.35" />
            </linearGradient>
          </defs>

          {/* Lines to each orbit icon (approximate positions matching the layout) */}
          {/* Top */}
          <line
            x1="50"
            y1="50"
            x2="50"
            y2="12"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
          {/* Top-right */}
          <line
            x1="50"
            y1="50"
            x2="82"
            y2="22"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
          {/* Right */}
          <line
            x1="50"
            y1="50"
            x2="92"
            y2="50"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
          {/* Bottom-right */}
          <line
            x1="50"
            y1="50"
            x2="82"
            y2="78"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
          {/* Bottom */}
          <line
            x1="50"
            y1="50"
            x2="50"
            y2="88"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
          {/* Bottom-left */}
          <line
            x1="50"
            y1="50"
            x2="18"
            y2="78"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
          {/* Left */}
          <line
            x1="50"
            y1="50"
            x2="8"
            y2="50"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
          {/* Top-left */}
          <line
            x1="50"
            y1="50"
            x2="18"
            y2="22"
            stroke="url(#lineGradient)"
            strokeWidth="0.6"
            strokeLinecap="round"
          />
        </svg>

        {/* Central glowing orb */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <div
            className="relative flex h-28 w-28 sm:h-32 sm:w-32 items-center justify-center rounded-full
              bg-[radial-gradient(circle_at_30%_28%,#FFF8F0_0%,#F8E0EC_40%,#E8D0F5_75%,#D8C0F0_100%)]
              shadow-[0_0_50px_-6px_rgba(232,180,200,0.45),0_0_28px_-4px_rgba(245,220,180,0.35)]
              ring-1 ring-white/70"
          >
            <span
              className="text-5xl sm:text-6xl select-none drop-shadow-sm"
              aria-hidden="true"
            >
              🤰
            </span>
          </div>
        </div>

        {/* Icon-only orbiting cards */}
        {orbitIcons.map((item) => (
          <div key={item.id} className={`absolute z-10 ${item.position}`}>
            <div
              className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full
                bg-gradient-to-br from-[#F8E9FF]/90 to-[#EEDCFA]/95
                border border-white/65
                shadow-[0_8px_24px_-8px_rgba(180,140,180,0.22)]
                backdrop-blur-md
                transition-all duration-300
                hover:scale-110 hover:border-white/90 hover:shadow-[0_0_28px_-4px_rgba(220,180,200,0.4)]"
            >
              <span
                className="text-2xl sm:text-3xl select-none"
                aria-hidden="true"
              >
                {item.icon}
              </span>
            </div>
          </div>
        ))}
      </div>
    </header>
  );
}